import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { ArrowLeft, Pencil, Trash, FilePdf, Printer, WhatsappLogo, MagnifyingGlass, CheckCircle, Receipt } from '@phosphor-icons/react';
import { toast } from 'sonner';

export default function Quotations() {
  const navigate = useNavigate();
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const [quotations, setQuotations] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filtered, setFiltered] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());

  useEffect(() => { fetchQuotations(); }, []);
  useEffect(() => {
    if (!searchQuery.trim()) { setFiltered(quotations); return; }
    const q = searchQuery.toLowerCase();
    setFiltered(quotations.filter(qt =>
      (qt.voucher_number || '').toLowerCase().includes(q) ||
      (qt.buyer_name || '').toLowerCase().includes(q) ||
      (qt.buyer_mobile || '').toLowerCase().includes(q)
    ));
  }, [searchQuery, quotations]);

  const fetchQuotations = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/quotations`);
      setQuotations(data);
      setSelectedIds(new Set());
    } catch (e) { console.error(e); }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/quotations/${id}`);
      toast.success('Quotation deleted');
      fetchQuotations();
    } catch (e) { toast.error('Error deleting quotation'); }
  };

  const handleDeleteSelected = async () => {
    if (selectedIds.size === 0) return;
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/quotations/bulk-delete`, { ids: Array.from(selectedIds) });
      toast.success(data.message);
      fetchQuotations();
    } catch (e) { toast.error('Error deleting selected quotations'); }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === filtered.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filtered.map(q => q.id)));
    }
  };

  const allSelected = filtered.length > 0 && selectedIds.size === filtered.length;

  const downloadPDF = async (id, vn) => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/quotations/${id}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Quotation_${vn}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('PDF downloaded');
    } catch (e) { toast.error('Error downloading PDF'); }
  };

  const shareWhatsApp = async (q) => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/quotations/${q.id}/pdf`, { responseType: 'blob' });
      const pdfBlob = new Blob([response.data], { type: 'application/pdf' });
      const pdfFile = new File([pdfBlob], `Quotation_${q.voucher_number}.pdf`, { type: 'application/pdf' });

      let shared = false;
      try {
        if (navigator.canShare && navigator.canShare({ files: [pdfFile] })) {
          await navigator.share({
            files: [pdfFile],
            title: `Quotation ${q.voucher_number} - Veer Electrical`,
            text: `Quotation ${q.voucher_number} from Veer Electrical (Loharchawl), Mumbai`,
          });
          shared = true;
        }
      } catch (shareErr) {
        if (shareErr.name !== 'AbortError') console.error('Share failed:', shareErr);
      }

      if (!shared) {
        const url = window.URL.createObjectURL(pdfBlob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Quotation_${q.voucher_number}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);

        const phone = q.buyer_mobile ? q.buyer_mobile.replace(/\D/g, '') : '';
        const fullPhone = phone.length === 10 ? `91${phone}` : phone;
        const msg = encodeURIComponent(`Quotation ${q.voucher_number} from Veer Electrical (Loharchawl), Mumbai.\nTotal: Rs.${q.grand_total?.toFixed(2)}\nPlease find the PDF attached.`);
        const waUrl = fullPhone ? `https://wa.me/${fullPhone}?text=${msg}` : `https://wa.me/?text=${msg}`;
        setTimeout(() => window.open(waUrl, '_blank'), 500);
        toast.info('PDF downloaded! Attach it in the WhatsApp chat.');
      }
    } catch (e) { toast.error('Error sharing'); }
  };

  const printQuotation = (quotation) => {
    const token = localStorage.getItem('access_token');
    window.location.href = `${BACKEND_URL}/api/quotations/${quotation.id}/pdf?token=${token}`;
    toast.info('PDF downloading — open it to print');
  };

  const editQuotation = (quotation) => {
    navigate('/create-quotation', { state: { editQuotation: quotation } });
  };

  const markAsBilled = async (id) => {
    try {
      await axios.put(`${BACKEND_URL}/api/quotations/${id}/mark-billed`);
      toast.success('Quotation marked as billed');
      fetchQuotations();
    } catch (e) { toast.error('Failed to mark as billed'); }
  };

  const formatDate = (d) => new Date(d).toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' });

  return (
    <div className="min-h-screen bg-[#F8F9FB]">
      <header className="bg-white border-b border-border shadow-sm">
        <div className="px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="outline" size="sm" onClick={() => navigate('/dashboard')} data-testid="back-to-dashboard">
              <ArrowLeft size={16} className="mr-1.5" /> Back
            </Button>
            <h1 className="text-xl sm:text-2xl font-semibold tracking-tight" data-testid="quotations-title">Non-Billed Quotations</h1>
          </div>
          <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white" onClick={() => navigate('/billed')} data-testid="go-to-billed">
            <Receipt size={14} className="mr-1" /> Billed Quotations
          </Button>
        </div>
      </header>

      <div className="p-4 sm:p-6 max-w-[1400px] mx-auto space-y-3">
        {/* Search */}
        <div className="flex items-center gap-2 bg-white border border-border rounded-sm shadow-sm px-3 py-2">
          <MagnifyingGlass size={16} className="text-muted-foreground" />
          <input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by party name, mobile, or quotation number..."
            className="flex-1 text-sm outline-none bg-transparent"
            data-testid="search-quotations-input"
          />
        </div>

        {/* Selected Actions Bar */}
        {selectedIds.size > 0 && (
          <div className="bg-red-50 border border-red-200 rounded-sm p-3 flex items-center justify-between" data-testid="selected-quotations-bar">
            <span className="text-sm font-medium text-red-700">{selectedIds.size} quotation(s) selected</span>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => setSelectedIds(new Set())} data-testid="clear-quotation-selection">
                Clear Selection
              </Button>
              <Button size="sm" className="bg-red-600 hover:bg-red-700 text-white" onClick={handleDeleteSelected} data-testid="delete-selected-quotations">
                <Trash size={14} className="mr-1.5" /> Delete Selected
              </Button>
            </div>
          </div>
        )}

        <div className="bg-white border border-border rounded-sm shadow-sm overflow-hidden">
          <div className="px-4 py-2 border-b border-border text-xs text-muted-foreground">
            {filtered.length} quotation(s) {searchQuery && `matching "${searchQuery}"`}
          </div>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-[#111827]">
                  <TableHead className="w-[40px]">
                    <input
                      type="checkbox"
                      checked={allSelected}
                      onChange={toggleSelectAll}
                      className="w-3.5 h-3.5 accent-white cursor-pointer"
                      data-testid="select-all-quotations"
                    />
                  </TableHead>
                  <TableHead className="font-medium text-white text-xs">Voucher No.</TableHead>
                  <TableHead className="font-medium text-white text-xs">Buyer Name</TableHead>
                  <TableHead className="font-medium text-white text-xs">Mobile</TableHead>
                  <TableHead className="font-medium text-white text-xs">Items</TableHead>
                  <TableHead className="font-medium text-white text-xs">Grand Total</TableHead>
                  <TableHead className="font-medium text-white text-xs">Date</TableHead>
                  <TableHead className="font-medium text-white text-xs w-[160px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center text-muted-foreground py-12 text-sm" data-testid="empty-quotations">
                      {searchQuery ? 'No matching quotations found.' : 'No quotations found. Create your first quotation from the dashboard.'}
                    </TableCell>
                  </TableRow>
                ) : (
                  filtered.map((q, idx) => (
                    <TableRow key={q.id} className={`${idx % 2 === 0 ? 'bg-white' : 'bg-[#F8F9FB]'} ${selectedIds.has(q.id) ? '!bg-red-50' : ''}`}>
                      <TableCell>
                        <input
                          type="checkbox"
                          checked={selectedIds.has(q.id)}
                          onChange={() => toggleSelect(q.id)}
                          className="w-3.5 h-3.5 accent-red-600 cursor-pointer"
                          data-testid={`select-quotation-${idx}`}
                        />
                      </TableCell>
                      <TableCell className="font-mono font-semibold text-[#0066FF] text-xs">{q.voucher_number}</TableCell>
                      <TableCell className="text-xs">{q.buyer_name || '-'}</TableCell>
                      <TableCell className="text-xs">{q.buyer_mobile || '-'}</TableCell>
                      <TableCell className="text-xs">{q.items?.length || 0}</TableCell>
                      <TableCell className="text-xs font-semibold">{q.grand_total?.toFixed(2)}</TableCell>
                      <TableCell className="text-xs">{formatDate(q.created_at)}</TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <button onClick={() => editQuotation(q)} className="text-muted-foreground hover:text-foreground p-1" title="Edit" data-testid={`edit-quotation-${idx}`}>
                            <Pencil size={14} />
                          </button>
                          <button onClick={() => printQuotation(q)} className="text-muted-foreground hover:text-foreground p-1" title="Print" data-testid={`print-quotation-${idx}`}>
                            <Printer size={14} />
                          </button>
                          <button onClick={() => downloadPDF(q.id, q.voucher_number)} className="text-muted-foreground hover:text-foreground p-1" title="PDF" data-testid={`download-pdf-${idx}`}>
                            <FilePdf size={14} />
                          </button>
                          <button onClick={() => shareWhatsApp(q)} className="text-[#25D366] hover:text-[#1DA851] p-1" title="WhatsApp" data-testid={`whatsapp-quotation-${idx}`}>
                            <WhatsappLogo size={14} weight="fill" />
                          </button>
                          <button onClick={() => markAsBilled(q.id)} className="text-green-500 hover:text-green-700 p-1" title="Mark as Billed" data-testid={`bill-quotation-${idx}`}>
                            <CheckCircle size={14} weight="bold" />
                          </button>
                          <button onClick={() => handleDelete(q.id)} className="text-red-400 hover:text-red-600 p-1" title="Delete" data-testid={`delete-quotation-${idx}`}>
                            <Trash size={14} />
                          </button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      </div>
    </div>
  );
}
